import { GET_ACTIVITY_LOG_FEED, GET_ALL_PROFILES_ACTIVITIES_INFO } from "../../graphql/activity";
import { useQuery } from "@apollo/client/react";
import React, { use, useCallback, useMemo } from "react";
import apolloClient from "../../configs/graphql.config";
import { Activity, ActivityType, useActivityListItems } from "../../components/ActivityListView";
import { useListGroupRenderItem } from "../../components/ListGroup";
import { useI18n } from "../../hooks/i18n-hooks";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { getActivityTitle, useActivityNavigationInfo } from "../../hooks/activities";
import { useNavigation } from "@react-navigation/native";
import EmptyScreen from "../../components/EmptyScreen";
import NoActivitiesImage from "../../assets/images/no-activities-image.svg";
import ScreenCard from "../ScreenCard";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";

type ActivitiesScreenProps = StackScreenProps<RootStackParamList>;

const UserLogsScreen = () => {
    const navigation = useNavigation<ActivitiesScreenProps["navigation"]>();
    const { liftProfile } = use(ProfileSwitcherManagerContext);

    const i18n = useI18n();
    const { data: logFeed, refetch: logRefresh } = useQuery(GET_ACTIVITY_LOG_FEED, {
        context: { liftProfile },
        variables: {
            limit: 100,
            offset: 0,
        },
    });

    const getActivityNavigationInfo = useActivityNavigationInfo();
    const [pressingActivity, setPressingActivity] = React.useState<string | undefined>(undefined);

    const activityLogs = useMemo(() => {
        const result = (logFeed?.getActivityLogFeed?.elements || []).map((log) => {
            return {
                id: log.id,
                name: log.activityName,
                activityKey: log.activityKey,
                title: getActivityTitle(log.title),
                text: log.text,
                createdAt: log.createdAt,
                badgeUrl: log.badgeUrl,
                priority: -1,
                at: new Date(log.createdAt),
                type: ActivityType.log,
                sourceName: log.sourceName,
            };
        });
        return result;
    }, [logFeed?.getActivityLogFeed?.elements]);

    const onPressLog = useCallback(
        (activity: Activity) => {
            try {
                setPressingActivity(activity.id);
                const navInfo = getActivityNavigationInfo(activity.name, activity.activityKey);
                if (navInfo) {
                    navigation.navigate(navInfo);
                }
            } finally {
                setPressingActivity(undefined);
            }
        },
        [getActivityNavigationInfo, navigation],
    );

    const refetch = useCallback(async () => {
        void apolloClient.refetchQueries({
            include: [GET_ALL_PROFILES_ACTIVITIES_INFO],
        });
        void logRefresh();
        return Promise.resolve();
    }, [logRefresh]);

    const items = useActivityListItems(activityLogs, {
        onSelectActivity: onPressLog,
        shadowActivityId: pressingActivity,
    });
    const renderRow = useListGroupRenderItem(items, { isItemsCheckable: false });
    const showList = activityLogs.length > 0;

    return (
        <ScreenCard
            refresh={refetch}
            isContentContainerFlex={activityLogs.length === 0}
            virtualizedItems={showList ? items : undefined}
            renderItem={showList ? renderRow : undefined}
        >
            {showList ? undefined : (
                <EmptyScreen
                    image={NoActivitiesImage}
                    text={i18n.t("screens.user.activity_log.no_activity")}
                />
            )}
        </ScreenCard>
    );
};

export default UserLogsScreen;
